import { Link } from 'react-router-dom';
import { Phone, ArrowUp, MapPin } from 'lucide-react';
import { FaFacebookF, FaInstagram, FaGoogle } from 'react-icons/fa';

const FOOTER_LOGO_URL = "https://customer-assets.emergentagent.com/job_5a91ec1d-c247-4280-a3f0-08c1958626f0/artifacts/3z2nenay_BNPH%20Logo%20with%2050th%20banner.png";
const ANNIVERSARY_LOGO_URL = "https://customer-assets.emergentagent.com/job_5a91ec1d-c247-4280-a3f0-08c1958626f0/artifacts/2tfz4y04_BNPH_50th%20ANNV%20Emblem.png";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#14366C] text-white" data-testid="footer">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-start">
          {/* Logo and Phone Column */}
          <div>
            <div className="pt-4">
              <a 
                href="tel:651-738-9354" 
                className="font-oswald text-2xl md:text-3xl font-bold hover:text-[#F58220] transition-colors"
              >
                (651) 738-9354
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="font-oswald font-bold text-lg uppercase mb-4 tracking-wide border-b border-gray-500 pb-2">
              Services
            </h3>
            <ul className="space-y-2">
              <li><Link to="/services/residential" className="text-gray-300 hover:text-[#F58220] transition-colors">Residential</Link></li>
              <li><Link to="/services/commercial" className="text-gray-300 hover:text-[#F58220] transition-colors">Commercial</Link></li>
              <li><Link to="/services/boilers" className="text-gray-300 hover:text-[#F58220] transition-colors">Boilers</Link></li>
              <li><Link to="/services/water-heaters" className="text-gray-300 hover:text-[#F58220] transition-colors">Water Heaters</Link></li>
            </ul>
          </div>

          {/* Our Company Column */}
          <div>
            <h3 className="font-oswald font-bold text-lg uppercase mb-4 tracking-wide border-b border-gray-500 pb-2">
              Our Company
            </h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-[#F58220] hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/our-company" className="text-gray-300 hover:text-[#F58220] transition-colors">Our Company</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-[#F58220] transition-colors">Services</Link></li>
              <li><Link to="/project-gallery" className="text-gray-300 hover:text-[#F58220] transition-colors">Project Gallery</Link></li>
              <li><Link to="/blog" className="text-gray-300 hover:text-[#F58220] transition-colors" data-testid="footer-blog-link">Blog</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-[#F58220] transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* 50th Anniversary Emblem */}
          <div className="flex flex-col items-center md:items-end">
            <img 
              src={ANNIVERSARY_LOGO_URL} 
              alt="50th Anniversary" 
              className="h-28 md:h-36 w-auto mb-6"
            />
          </div>
        </div>

        {/* Contact Row */}
        <div className="border-t border-gray-500 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            {/* Contact Button and Address */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <Link 
                to="/contact" 
                className="bg-white text-[#14366C] font-oswald font-bold uppercase px-8 py-3 hover:bg-gray-100 transition-colors"
              >
                Contact Us
              </Link>
              <div className="flex items-start gap-2 text-gray-300">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <address className="not-italic">
                  7012 6th St. N.<br />
                  Oakdale, MN 55128
                </address>
              </div>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/BNPHService/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#F58220] transition-colors" aria-label="Facebook">
                <FaFacebookF className="w-5 h-5" />
              </a>
              <a href="https://share.google/kpP8G2g3uSDLnw9pe" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#F58220] transition-colors" aria-label="Google">
                <FaGoogle className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/brucenelsonph/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#F58220] transition-colors" aria-label="Instagram">
                <FaInstagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#0d2a52] bg-[#0d2a52]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">© 2026 BNPH Services LLC</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <Link 
              to="/terms-conditions" 
              className="text-gray-400 hover:text-[#F58220] transition-colors"
              data-testid="footer-terms-link"
            >
              Terms & Conditions
            </Link>
            <span className="text-gray-600">|</span>
            <Link 
              to="/privacy-policy" 
              className="text-gray-400 hover:text-[#F58220] transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-600">|</span>
            <Link 
              to="/website-accessibility" 
              className="text-gray-400 hover:text-[#F58220] transition-colors"
              data-testid="footer-accessibility-link"
            >
              WCAG Accessibility Statement
            </Link>
            <span className="text-gray-600">|</span>
            <span className="text-gray-400">
              Website Designed By{' '}
              <a 
                href="https://bp.agency" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-bold text-white hover:text-[#F58220] transition-colors"
              >
                Brand Partners
              </a>
            </span>
          </div>
          <button 
            onClick={scrollToTop}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            data-testid="go-to-top"
          >
            Go to Top <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
